import { Box, Button, Flex, Heading, HStack } from '@chakra-ui/react';
import { ActivityFragmentFragment } from '../../../generated/types';
import React, { useMemo } from 'react';
import { useStore } from '../../../store/provider';
import Wrapper from '../Wrapper';
import PageHero from '../../Layout/PageHero';
import getIconForCategoryKey from '../../../utils/getIconForCategoryKey';
import getAppComponent from '../../../utils/getAppComponent';
import scrollToElement from '../../../utils/scrollToElement';
import Link from 'next/link';
import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';
import differenceInDays from 'date-fns/differenceInDays';
import { AuthorAndDate } from '../../Common/AuthorAndDate';
import Container from '../../Layout/Container';
import { StaticAdSlot } from '../../Common/StaticAdSlot';

interface EbikeAtlasActivityIndexProps {
  data: ActivityFragmentFragment;
}

const EbikeAtlasActivityIndex: React.FC<EbikeAtlasActivityIndexProps> = ({
  data,
}) => {
  const store = useStore();
  const Icon = getIconForCategoryKey(data.category.key);
  const hotel = data.hotels?.items?.length > 0 ? data.hotels.items[0] : null;

  const subtitle = [data.country?.name, data.region?.name]
    .filter((i) => i)
    .join(' | ');

  const ActivityContent = getAppComponent(
    process.env.APP_ID!,
    store.config.usesNewLayout,
    'ActivityContent'
  );

  const shouldShowDate = useMemo(() => {
    if (data.updatedAt) {
      const parsed = parseISO(data.updatedAt);
      const diff = differenceInDays(new Date(), parsed);

      if (diff <= 365) {
        return true;
      }
    }

    return false;
  }, [data]);

  return (
    <Wrapper typename={data.__typename}>
      <PageHero
        darken
        imageUrl={data.teaserImage?.hero.url}
        imageTitle={data.teaserImage?.imageTitle}
        imageAlt={data.teaserImage?.imageAlt}
        imageCopyright={data.teaserImage?.copyright}
        copyrightPosition="top"
      >
        <Box>
          <Flex align="center">
            {Icon && (
              <Box
                padding={2}
                borderRadius="lg"
                display="inline-flex"
                fontSize={{
                  base: 'md',
                  md: '2xl',
                }}
                border="2px solid"
                borderColor="#fff"
                color="textColor"
              >
                <Icon />
              </Box>
            )}
            <Box
              color="textColor"
              fontSize={{
                base: 'md',
                md: 'xl',
              }}
              marginLeft={4}
            >
              {subtitle}
            </Box>
          </Flex>
          <Heading
            my={4}
            as="h1"
            fontSize={{
              base: '3xl',
              md: '5xl',
            }}
          >
            {data.title}
          </Heading>
          {hotel && (
            <Box
              fontSize={{
                base: 'lg',
                md: 'xl',
              }}
              mb={4}
            >
              <Box>Ausgangspunkt {hotel.title}</Box>
              <Box>in {hotel.city}</Box>
            </Box>
          )}
          {data.length > 0 && (
            <Heading fontSize="2xl" mb={2} mt={-4}>
              {data.length}km
            </Heading>
          )}

          <HStack>
            {data.gpx && (
              <Box fontSize="2xl">
                <a target="_blank" download href={`${data.gpx.url}`}>
                  <Button size="sm" as="span" colorScheme="blue">
                    GPX-Download
                  </Button>
                </a>
              </Box>
            )}

            <Box fontSize="2xl">
              <Button
                size="sm"
                as="span"
                colorScheme="blue"
                onClick={() => scrollToElement('#comment-wrapper')}
              >
                Kommentar schreiben
              </Button>
            </Box>
          </HStack>

          <Flex alignItems="center">
            <AuthorAndDate
              authors={data.authors}
              date={data.updatedAt}
              renderDate={shouldShowDate}
            />
            <>
              <Box mx={2}>|</Box>
              <Box fontSize="sm">
                {`${data.totalComments} Kommentar${
                  data.totalComments > 1 ? 'e' : ''
                }`}
              </Box>
            </>
          </Flex>
        </Box>
      </PageHero>

      <Container>
        <StaticAdSlot adKey="leaderboard" />
      </Container>
      <ActivityContent data={data} />
    </Wrapper>
  );
};

export default EbikeAtlasActivityIndex;
